import {
  Button,
  ExpandablePanel,
  Modal,
  PillButtonSampleText,
  SectionEditor,
  VerticalProgressBarCirclesWithText,
} from "@truevault/aquilo";
import React, { useCallback, useState } from "react";
import { useUpdateNoticeApi } from "../../../../components/privacyCenter/EditPrivacyCenter";
import { PrivacyCenterNoticeDto } from "../../../../../common/service/server/dto/PrivacyCenterDto";
import { Form } from "react-final-form";
import _ from "lodash";

export function FinancialIncentiveNoticeEditor({
  organizationId,
  privacyCenterId,
  refreshNotice,
  draftNotice,
}: {
  organizationId: string;
  privacyCenterId: string;
  refreshNotice: () => Promise<void>;
  draftNotice: PrivacyCenterNoticeDto;
}) {
  const financialIncentiveSection = draftNotice.sections.find(
    (section) => section.anchor === "notice-of-financial-incentive",
  );

  const [updatedContent, setUpdatedContent] = useState<string>(financialIncentiveSection.content);
  const { updateNotice } = useUpdateNoticeApi();

  const [sampleTextModalDisplayed, setSampleTextModalDisplayed] =
    useState<FinancialIncentiveSampleModalVersion | null>(null);

  const debouncedSave = useCallback(
    _.debounce(async (newContent: string) => {
      let updateNoticeBody = {
        organizationId: organizationId,
        privacyCenterId: privacyCenterId,
        noticeId: draftNotice.publicId,
        updateNoticeBody: {
          sections: [{ id: financialIncentiveSection.id, content: newContent }],
        },
      };

      await updateNotice(updateNoticeBody);

      await refreshNotice();
    }, 750),
    [],
  );

  const handleSectionChange = useCallback(
    (newContent: string) => {
      setUpdatedContent(newContent);
      debouncedSave(newContent);
    },
    [setUpdatedContent],
  );

  return (
    <>
      <div style={{ maxWidth: "764px" }}>
        <div className="tw-boundary">
          <ExpandablePanel
            title="Your privacy policy must include a description of key terms of your incentive program so that consumers know what they're signing up for. Specifically, you need to provide:"
            titleFontSize="base"
            widthClass="w-full"
          >
            {/* TODO: we should really just have the ExpandablePanel handle the margin */}
            <div className="mt-6">
              <VerticalProgressBarCirclesWithText
                steps={[
                  {
                    name: "Summary of the incentive, including the terms",
                    description:
                      'Ex: "Consumers who sign up for our marketing emails/SMS texts receive a 10% discount on their first purchase."',
                    status: "complete",
                  },
                  {
                    name: "Description of the opt-in process",
                    description:
                      'Ex: "To opt in, a consumer must enter their email address/phone number into the form and consent to receive emails in exchange for a discount provided via coupon code."',
                    status: "complete",
                  },
                  {
                    name: "Statement on how to withdraw from the program",
                    description:
                      'Ex: "A consumer may unsubscribe from our marketing emails by using the unsubscribe link in the email footer/replying STOP via text at any time."',
                    status: "complete",
                  },
                  {
                    name: "Description of the value of the consumer's data",
                    description:
                      'Ex: "We calculate the value of the offer and financial incentive by using the expense related to the offer."',
                    status: "complete",
                  },
                ]}
              />
            </div>
          </ExpandablePanel>

          <div className="mt-8">
            <div className="font-medium text-[18px]">
              You can use the examples below to create your notice
            </div>

            <div className="flex mt-2 gap-sm">
              <PillButtonSampleText
                text="Discount for Email Signup Template"
                onClick={() => setSampleTextModalDisplayed("email-signup-example")}
              />

              <PillButtonSampleText
                text="Loyalty Program Example"
                onClick={() => setSampleTextModalDisplayed("loyalty-program-example")}
              />
            </div>
          </div>
        </div>

        {/* Important: make sure the actual section editor is outside of a tw-boundary ancestor in the DOM */}
        <div style={{ marginTop: "48px" }}>
          <Form
            onSubmit={() => {}}
            render={(_formRenderProps) => (
              <SectionEditor
                hideNavigationCheckbox={true}
                hideSideControls={true}
                addToNavigation={false}
                header={financialIncentiveSection.header}
                content={updatedContent}
                locked={false}
                placeholder={financialIncentiveSection.placeholder}
                onPositionUp={() => {}}
                onPositionDown={() => {}}
                moveDisabled={true}
                moveUpDisabled={true}
                moveDownDisabled={true}
                deleteDisabled={true}
                onShowInNavigationCheckboxToggle={() => {}}
                onDelete={() => {}}
                onInputChange={({ content }) => {
                  handleSectionChange(content);
                }}
                onUnlock={() => {}}
                enablePrivacyCenterToolbar={true}
              />
            )}
          />
        </div>

        {sampleTextModalDisplayed !== null && (
          <FinancialIncentivesSampleModal
            modalType={sampleTextModalDisplayed}
            closeModal={() => setSampleTextModalDisplayed(null)}
          />
        )}
      </div>
    </>
  );
}

type FinancialIncentiveSampleModalVersion = "email-signup-example" | "loyalty-program-example";

const EMAIL_SIGNUP_EXAMPLE_TEXT = `Consumers who sign up for our marketing emails/SMS texts receive a 10% discount on their first purchase. To opt in, a consumer must enter their email address/phone number into the form and consent to receive emails in exchange for a discount provided via coupon code. A consumer may unsubscribe from our marketing emails by using the unsubscribe link in the email footer/replying STOP via text at any time. We calculate the value of the offer and financial incentive by using the expense related to the offer.`;

const LOYALTY_PROGRAM_EXAMPLE_TEXT = `Our loyalty program rewards consumers for their loyalty. Members who sign up via our form (add link) provide their name, email and account number. Members receive certain promotions, discounts and points annually or when they make purchases. These points can be redeemed for discounts on future purchases. To opt-out of the Loyalty Program, email us at: [email]. We calculate the value of the offer and financial incentive by using the expense related to the offer.`;

function FinancialIncentivesSampleModal({
  closeModal,
  modalType,
}: {
  closeModal: () => void;
  modalType: FinancialIncentiveSampleModalVersion;
}) {
  const titleText =
    modalType === "email-signup-example"
      ? "Discount for Email Signup Example"
      : "Loyalty Program Example";
  const exampleText =
    modalType === "email-signup-example" ? EMAIL_SIGNUP_EXAMPLE_TEXT : LOYALTY_PROGRAM_EXAMPLE_TEXT;

  const [showCopySuccessMessage, setShowCopySuccessMessage] = useState(false);

  return (
    <Modal
      title={titleText}
      alignText="left"
      showOverlay={true}
      closeButton={true}
      closeOnOverlayClick={true}
      onClose={closeModal}
      maxPanelWidth="max-w-3xl"
      contentPadding="px-[24px]"
      buttonPadding="p-0"
    >
      <div
        className="text-[18px]"
        style={{ textAlign: "left", padding: "32px", background: "#F5F4F9" }}
      >
        {exampleText}
      </div>
      <div className="mt-8 text-right">
        <Button
          color="primary"
          onClick={() => {
            navigator.clipboard.writeText(exampleText);
            setShowCopySuccessMessage(true);
            setTimeout(() => {
              setShowCopySuccessMessage(false);
            }, 2500);
          }}
        >
          <div className="flex items-center">
            {showCopySuccessMessage ? (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  style={{ width: "15px", height: "17px" }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19.916 4.626a.75.75 0 0 1 .208 1.04l-9 13.5a.75.75 0 0 1-1.154.114l-6-6a.75.75 0 0 1 1.06-1.06l5.353 5.353 8.493-12.74a.75.75 0 0 1 1.04-.207Z"
                  />
                </svg>
                <div className="ml-2">Copied</div>
              </>
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  style={{ width: "15px", height: "17px" }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 0 1-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 0 1 1.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 0 0-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 0 1-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 0 0-3.375-3.375h-1.5a1.125 1.125 0 0 1-1.125-1.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H9.75"
                  />
                </svg>
                <div className="ml-2">Copy Text</div>
              </>
            )}
          </div>
        </Button>
      </div>
    </Modal>
  );
}
